import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout, message, Spin } from 'antd';
import { useAuthStore } from './stores/authStore';
import Navbar from './components/Layout/Navbar';
import Sidebar from './components/Layout/Sidebar';
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import ProjectCenter from './pages/Projects/ProjectCenter';
import ProjectDetail from './pages/Projects/ProjectDetail';
import TaskManagement from './pages/Tasks/TaskManagement';
import MyTasks from './pages/Tasks/MyTasks';
import DailyLog from './pages/DailyLog/DailyLog';
import TeamWorkload from './pages/Analytics/TeamWorkload';
import ProjectProgress from './pages/Analytics/ProjectProgress';
import ProjectLogs from './pages/Analytics/ProjectLogs';
import PersonalStats from './pages/Analytics/PersonalStats';
import ProfitLossAnalysis from './pages/Analytics/ProfitLossAnalysis';
import ProjectAnalysis from './pages/Analytics/ProjectAnalysis';
import UserProfile from './pages/Profile/UserProfile';
import OvertimeCompStats from './pages/Analytics/OvertimeCompStats';
import LogStats from './pages/Analytics/LogStats';
import UserManagement from './pages/Admin/UserManagement';
import TaskTemplateManagement from './pages/Admin/TaskTemplateManagement';
import SystemParameterManagement from './pages/Admin/SystemParameterManagement';
import OvertimeManagement from './pages/Admin/OvertimeManagement';
import OvertimeApplication from './pages/OvertimeApplication';
import ApiTest from './pages/Debug/ApiTest';
import CompensatoryApplication from './pages/CompensatoryApplication';
import CompensatoryManagement from './pages/Admin/CompensatoryManagement';
// 新增：学习论坛页面
import ForumCategory from './pages/Forum/ForumCategory';
import ExhibitionCheckin from './pages/Forum/ExhibitionCheckin';
import ArtProduction from './pages/ArtProduction/ArtProduction';
import VendorLogin from './pages/Vendor/VendorLogin';
import VendorRegister from './pages/Vendor/VendorRegister';
import VendorOrders from './pages/Vendor/VendorOrders';
import { configureVendorAxios, getVendorSession } from './stores/vendorAuth';

// ===== 移动端路由（懒加载）BEGIN =====
// 中文函数级注释：通过 React.lazy 实现移动端页面的代码分割与按需加载，提升移动端首屏性能
const MobileLayout = React.lazy(() => import('./mobile/layout/MobileLayout'));
const MobileProtectedRoute = React.lazy(() => import('./mobile/MobileProtectedRoute'));
const MLogin = React.lazy(() => import('./mobile/pages/Login'));
const MHome = React.lazy(() => import('./mobile/pages/Home'));
const MLogStats = React.lazy(() => import('./mobile/pages/LogStats'));
const MProjectProgress = React.lazy(() => import('./mobile/pages/ProjectProgress'));
const MOvertimeReview = React.lazy(() => import('./mobile/pages/OvertimeReview'));
const MProjectTaskList = React.lazy(() => import('./mobile/pages/ProjectTaskList'));
const MForumExhibitionCheckin = React.lazy(() => import('./mobile/pages/ForumExhibitionCheckin'));
const MForumNewMaterialProcess = React.lazy(() => import('./mobile/pages/ForumNewMaterialProcess'));
// ===== 移动端路由（懒加载）END =====

const { Content } = Layout;

// 路由保护组件
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isInitialized } = useAuthStore();
  
  // 等待初始化完成
  if (!isInitialized) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column' 
      }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, color: '#666' }}>正在初始化...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// 主布局组件
const MainLayout = ({ children }) => {
  
  return (
    <Layout className="app-layout">
      <Navbar />
      <Layout>
        <Sidebar />
        <Layout>
          <Content className="main-content">
            {children}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

const VendorProtectedRoute = ({ children }) => {
  const session = getVendorSession();
  if (!session?.token) return <Navigate to="/vendor/login" replace />;
  configureVendorAxios();
  return children;
};

function App() {
  const { isAuthenticated, isInitialized, initializeAuth, user } = useAuthStore();

  // 初始化认证状态
  React.useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // 全局消息配置
  React.useEffect(() => {
    message.config({
      top: 100,
      duration: 3,
      maxCount: 3,
    });
  }, []);

  return (
    <div className="App">
      <Routes>
        <Route path="/vendor/login" element={<VendorLogin />} />
        <Route path="/vendor/register" element={<VendorRegister />} />
        <Route path="/vendor/orders" element={<VendorProtectedRoute><VendorOrders /></VendorProtectedRoute>} />
        {/* ===== 移动端路由组 BEGIN ===== */}
        <Route
          path="/m/login"
          element={
            <React.Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div>}>
              {!isInitialized ? (
                <div style={{ 
                  height: '100vh', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column' 
                }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 16, color: '#666' }}>正在加载...</div>
                </div>
              ) : isAuthenticated ? (
                <Navigate to="/m" replace />
              ) : (
                <MLogin />
              )}
            </React.Suspense>
          }
        />
        <Route
          path="/m"
          element={
            <React.Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div>}>
              <MobileProtectedRoute>
                <MobileLayout>
                  <MHome />
                </MobileLayout>
              </MobileProtectedRoute>
            </React.Suspense>
          }
        />
        <Route
          path="/m/log-stats"
          element={
            <React.Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div>}>
              <MobileProtectedRoute>
                <MobileLayout>
                  <MLogStats />
                </MobileLayout>
              </MobileProtectedRoute>
            </React.Suspense>
          }
        />
        <Route
          path="/m/projects"
          element={
            <React.Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div>}>
              <MobileProtectedRoute>
                <MobileLayout>
                  <MProjectProgress />
                </MobileLayout>
              </MobileProtectedRoute>
            </React.Suspense>
          }
        />
        <Route
          path="/m/forum/exhibition-checkin"
          element={
            <React.Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div>}>
              <MobileProtectedRoute>
                <MobileLayout>
                  <MForumExhibitionCheckin />
                </MobileLayout>
              </MobileProtectedRoute>
            </React.Suspense>
          }
        />
        <Route
          path="/m/forum/new-material-process"
          element={
            <React.Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div>}>
              <MobileProtectedRoute>
                <MobileLayout>
                  <MForumNewMaterialProcess />
                </MobileLayout>
              </MobileProtectedRoute>
            </React.Suspense>
          }
        />
        <Route
          path="/m/projects/:id/tasks"
          element={
            <React.Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div>}>
              <MobileProtectedRoute>
                <MobileLayout>
                  <MProjectTaskList />
                </MobileLayout>
              </MobileProtectedRoute>
            </React.Suspense>
          }
        />
        <Route
          path="/m/overtime-review"
          element={
            <React.Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div>}>
              <MobileProtectedRoute>
                <MobileLayout>
                  <MOvertimeReview />
                </MobileLayout>
              </MobileProtectedRoute>
            </React.Suspense>
          }
        />
        {/* ===== 移动端路由组 END ===== */}
        {/* 登录页面 */}
        <Route 
          path="/login" 
          element={
            !isInitialized ? (
              <div style={{ 
                height: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                flexDirection: 'column' 
              }}>
                <Spin size="large" />
                <div style={{ marginTop: 16, color: '#666' }}>正在加载...</div>
              </div>
            ) : isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <Login />
            )
          } 
        />
        
        {/* 受保护的路由 */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/projects" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProjectCenter />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/projects/:id" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProjectDetail />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/tasks" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <TaskManagement />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/my-tasks" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyTasks />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/daily-log" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <DailyLog />
              </MainLayout>
            </ProtectedRoute>
          } 
        />

        <Route
          path="/art-production"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ArtProduction />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* 学习论坛 */}
        <Route 
          path="/forum/ai-design" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <ForumCategory category="ai-design" title="AI设计" />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/forum/new-material-process" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <ForumCategory category="new-material-process" title="新材料工艺" />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/forum/exhibition-checkin" 
          element={
            <ProtectedRoute>
              <MainLayout>
                {/** 展馆打卡：独立页面 */}
                <ExhibitionCheckin />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/analytics/team-workload" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <TeamWorkload />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/analytics/project-progress" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProjectProgress />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/analytics/project-logs" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProjectLogs />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* 新增：日志统计 */}
        <Route 
          path="/analytics/log-stats" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <LogStats />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/analytics/personal-stats" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <PersonalStats />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/analytics/profit-loss" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProfitLossAnalysis />
              </MainLayout>
            </ProtectedRoute>
          } 
        />

        {/* 新增：项目分析路由 */}
        <Route 
          path="/analytics/project-analysis" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProjectAnalysis />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <UserProfile />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/admin/users" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <UserManagement />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/admin/templates" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <TaskTemplateManagement />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/admin/system-parameters" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <SystemParameterManagement />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/admin/overtime" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <OvertimeManagement />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* 新增：调休审批路由 */}
        <Route 
          path="/admin/compensatory" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <CompensatoryManagement />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/analytics/overtime-comp" 
          element={
            <ProtectedRoute>
              <MainLayout>
                {/* 仅管理员与设计部经理可访问 */}
                {['admin', 'manager'].includes(user?.role) ? (
                  <OvertimeCompStats />
                ) : (
                  <Navigate to="/" replace />
                )}
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/overtime" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <OvertimeApplication />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* 申请调休：补充 MainLayout 以包含 Sidebar */}
        <Route path="/compensatory" element={<ProtectedRoute><MainLayout><CompensatoryApplication /></MainLayout></ProtectedRoute>} />
        {/* 兼容旧路径：同样包裹 MainLayout，确保左侧菜单显示 */}
        <Route path="/overtime-comp-stats" element={<ProtectedRoute><MainLayout>{['admin','manager'].includes(user?.role) ? <OvertimeCompStats /> : <Navigate to="/" replace />}</MainLayout></ProtectedRoute>} />
        <Route path="/debug/api-test" 
          element={
            <ProtectedRoute>
              <MainLayout>
                <ApiTest />
              </MainLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* 默认重定向 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;
